import { useCallback, useEffect, useReducer } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { CustomCategoriesWizard } from "app/components/customCategoriesWizard/CustomCategoriesWizard";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import type { ICountryObject } from "app/services/CountryService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { EDIT_INDUSTRY_ON_ARRIVAL_PARAM } from "../../MarketBuilder.types";

// Clearing the param would be a router transition, which tracks the page view again. It stays
// set instead, so these keep the editor one-shot when the Overview remounts with it.
const closedIndustryIds = new Set<string>();
const trackedIndustryIds = new Set<string>();

type SavedIndustry = {
    ctrl: { country?: ICountryObject };
};

/** Opens the industry the AI builder just handed over, read-only first, once, on the Overview. */
export const CreatedIndustryEditorOnArrival = () => {
    const { params } = useCurrentStateAndParams();
    const editIndustryId = (params[EDIT_INDUSTRY_ON_ARRIVAL_PARAM] as string | null | undefined) ?? null;
    const { primaryCategory } = useCategoriesComparison();
    const [, rerender] = useReducer((renders: number) => renders + 1, 0);

    const editedIndustryId =
        editIndustryId && editIndustryId === primaryCategory?.categoryId && !closedIndustryIds.has(editIndustryId)
            ? editIndustryId
            : null;

    useEffect(() => {
        if (!editedIndustryId || trackedIndustryIds.has(editedIndustryId)) {
            return;
        }

        trackedIndustryIds.add(editedIndustryId);
        allTrackers.dispatchTrackEvent("Pop Up", "open", "marketBuilder/aiIndustryEditor", {
            industryId: editedIndustryId,
        });
    }, [editedIndustryId]);

    const handleClose = useCallback(() => {
        if (editedIndustryId) {
            closedIndustryIds.add(editedIndustryId);
        }
        rerender();
    }, [editedIndustryId]);

    // `stayOnPage` saves without navigating, so a country changed in the editor has to be
    // carried into the Overview's own country filter here.
    const handleSave = useCallback(
        ({ ctrl: { country } }: SavedIndustry) => {
            handleClose();

            if (country && String(country.id) !== String(params.country)) {
                void proNavigator.updateParams({ country: country.id });
            }
        },
        [handleClose, params.country],
    );

    if (!editedIndustryId) {
        return null;
    }

    // Keyed because the wizard reads its industry only on mount, and another hand-off can
    // arrive while it is open.
    return (
        <CustomCategoriesWizard
            key={editedIndustryId}
            isOpen={true}
            onClose={handleClose}
            wizardProps={{
                customCategoryId: editedIndustryId,
                stayOnPage: true,
                initialReadOnly: true,
                onSave: handleSave,
            }}
        />
    );
};
